import React, { useEffect, useState } from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import authService from '../services/authService';
import AccessDenied from './AccessDenied';

interface RoleBasedRouteProps {
  children: React.ReactElement;
  allowedRoles: string[]; // Roles that can access this route
}

/**
 * RoleBasedRoute Component
 * 
 * Protects routes based on user's selected role.
 * Redirects to appropriate page or shows error if role doesn't match.
 */
const RoleBasedRoute: React.FC<RoleBasedRouteProps> = ({ children, allowedRoles }) => {
  const location = useLocation();
  const [shouldRedirect, setShouldRedirect] = useState(false);
  const user = authService.getUserData();
  const selectedRole = user?.role || '';

  // Check if user's role is allowed to access this route
  const hasAccess = allowedRoles.includes(selectedRole);

  // Determine redirect path based on role
  const getRedirectPath = () => {
    if (selectedRole === 'admin') {
      return '/admin/salaries';
    } else if (selectedRole === 'project_owner') {
      return '/dashboard';
    }
    return '/dashboard';
  };

  // Auto-redirect after showing error message
  useEffect(() => {
    if (!hasAccess) {
      const timer = setTimeout(() => {
        setShouldRedirect(true);
      }, 3000); // Show error for 3 seconds before redirecting

      return () => clearTimeout(timer);
    }
  }, [hasAccess]);

  // If no access, show error message and redirect
  if (!hasAccess) {
    if (shouldRedirect) {
      return <Navigate to={getRedirectPath()} replace />;
    }

    const roleDisplayName = selectedRole === 'admin' ? 'Admin' : 
                           selectedRole === 'project_owner' ? 'Project Owner' : 
                           selectedRole || 'Unknown';
    
    // Determine what section they tried to access
    const attemptedSection = location.pathname.startsWith('/admin') ? 'Admin section' :
                            location.pathname.startsWith('/dashboard') || location.pathname === '/' ? 'Project Budgeting section' :
                            location.pathname.startsWith('/saved-dashboard') ? 'Saved Project Analysis section' :
                            'this section';
    
    return (
      <AccessDenied
        title="Access Denied"
        message={`You don't have permission to access ${attemptedSection}. Your current role (${roleDisplayName}) does not have access to this page. You will be redirected to your dashboard in a few seconds...`}
      />
    );
  }

  // User has access, render the route
  return children;
};

export default RoleBasedRoute;

